import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo
} from 'react';
import { sanitizeUserFacingError } from '../utils/userFacingError';
import { api } from '../utils/api';
import { idsMatch } from '../utils/catalogIds';
import { readCatalogCache, writeCatalogCache, clearStaleCatalogSessionCache } from '../utils/catalogCache';
import {
  parseSubcategoryFromProduct,
  shapeProductForStorefront,
  filterBrowsableProducts,
  filterRawCatalogProducts
} from '../utils/catalogProductShape';
import { setStoreOutletsForChatLookup } from '../utils/vendorAvailability';
import { normalizeOutletsForChatLookup } from '../utils/storeChatLookup';
import { normalizeOutlet } from '../utils/outletDisplay';
import {
  buildCategoryMenuTreeByCategory,
  flattenLeafSubcategories,
  formatCatalogDisplayName,
  sortCategoriesAlphabetically,
} from '../utils/categoryMenuTree';

const DataContext = createContext();

// When REACT_APP_USE_CATALOG=true, storefront uses same source as admin (Railway via /api/catalog).
// Otherwise uses MongoDB (/api/products, /api/categories). Prioritize catalog so storefront matches admin.
const useCatalog = process.env.REACT_APP_USE_CATALOG === 'true';
const productsPath = useCatalog ? '/catalog/products' : '/products';
const categoriesPath = useCatalog ? '/catalog/categories' : '/categories';

/** Page size for public catalog — slim card projection keeps payloads small. */
const CATALOG_PAGE_SIZE = Math.max(
  50,
  parseInt(process.env.REACT_APP_CATALOG_PAGE_SIZE || '100', 10)
);

/** Let banner + homepage section APIs claim the network before the full catalog. */
function yieldToHomepagePaint() {
  return new Promise((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }
    const finish = () => {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => resolve(), { timeout: 400 });
      } else {
        setTimeout(resolve, 50);
      }
    };
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(finish));
    } else {
      finish();
    }
  });
}

/** Default 3m: refresh catalog in background so new admin/deal-fetch products appear sooner. */
const BACKGROUND_POLL_MS = Math.max(
  60_000,
  parseInt(process.env.REACT_APP_DATA_BACKGROUND_REFRESH_MS || '180000', 10)
);
/** After tab focus, skip refresh if we already refreshed recently (reduces duplicate traffic). */
const MIN_TAB_FOCUS_REFRESH_GAP_MS = Math.max(
  15_000,
  parseInt(process.env.REACT_APP_DATA_MIN_FOCUS_REFRESH_MS || '60000', 10)
);

function normalizeCatalogText(value) {
  return String(value || '').trim().toLowerCase();
}

function mergeSubcategoryMaps(primary = {}, secondary = {}) {
  const out = {};
  const push = (catKey, sub) => {
    if (!catKey || !sub) return;
    const key = String(catKey).trim();
    if (!key) return;
    if (!out[key]) out[key] = [];
    const id = sub.id != null ? String(sub.id).trim() : '';
    const rid = sub.railwayId != null ? String(sub.railwayId).trim() : '';
    const nm = normalizeCatalogText(sub.name);
    const exists = out[key].some((x) => {
      const xid = x.id != null ? String(x.id).trim() : '';
      const xrid = x.railwayId != null ? String(x.railwayId).trim() : '';
      const xnm = normalizeCatalogText(x.name);
      return (id && xid && id === xid) || (rid && xrid && rid === xrid) || (nm && xnm && nm === xnm);
    });
    if (!exists) out[key].push(sub);
  };

  [secondary, primary].forEach((src) => {
    Object.entries(src || {}).forEach(([catKey, subs]) => {
      (subs || []).forEach((sub) => push(catKey, sub));
    });
  });

  Object.keys(out).forEach((k) => {
    out[k].sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
  });
  return out;
}

function buildSubcategoriesFromProducts(productList = [], categoryList = []) {
  const byCat = {};
  const byName = {};
  (categoryList || []).forEach((c) => {
    const cid = String(c.id || c._id || '').trim();
    const cname = normalizeCatalogText(c.name);
    if (cid && cname) byName[cname] = cid;
  });

  const add = (catKey, item) => {
    const key = String(catKey || '').trim();
    if (!key || !item || !item.name) return;
    if (!byCat[key]) byCat[key] = [];
    byCat[key].push(item);
  };

  (productList || []).forEach((p) => {
    const categoryKey = String(
      p.category || p.categoryId || byName[normalizeCatalogText(p.categoryName)] || ''
    ).trim();
    if (!categoryKey) return;

    const parsedSub = parseSubcategoryFromProduct(p);
    const subId = String(parsedSub.subCategory || '').trim();
    const subRailwayId = String(parsedSub.subCategoryId || '').trim();
    const subName = String(parsedSub.subCategoryName || '').trim();

    if (!subName && !subId && !subRailwayId) return;
    add(categoryKey, {
      id: subId || subRailwayId || subName,
      railwayId: subRailwayId || null,
      name: formatCatalogDisplayName(subName || subId || subRailwayId),
      slug: (subName || subId || subRailwayId || '').toLowerCase().replace(/\s+/g, '-')
    });
  });

  Object.keys(byCat).forEach((k) => {
    byCat[k] = byCat[k].filter(Boolean);
  });
  return byCat;
}

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategoriesByCategory, setSubcategoriesByCategory] = useState({});
  const [categoryMenuTreeByCategory, setCategoryMenuTreeByCategory] = useState({});
  const [categoriesForGrid, setCategoriesForGrid] = useState([]);
  const [categoriesForNav, setCategoriesForNav] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialCatalogLoaded, setInitialCatalogLoaded] = useState(false);
  const [outlets, setOutlets] = useState([]);
  const [initialOutletsLoaded, setInitialOutletsLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);

  const productsFetchInflight = useRef(null);
  const categoriesFetchInflight = useRef(null);
  const lastTabRefreshAttemptRef = useRef(0);

  useEffect(() => {
    clearStaleCatalogSessionCache();
  }, []);

  const transformProduct = shapeProductForStorefront;

  const applyTransformedCategories = useCallback((categoriesData, categoriesFromMongo = true) => {
    const useCatalogCategoryFlags = useCatalog && !categoriesFromMongo;
    const transformedCategories = categoriesData.map((category, index) => ({
      id: category._id || category.id,
      _id: category._id || category.id,
      name: formatCatalogDisplayName(category.name),
      slug: category.slug || String(category.name || '').toLowerCase().replace(/\s+/g, '-'),
      image:
        category.categoryImage ||
        category.image ||
        `/images/categories/${category.slug || String(category.name || '').toLowerCase()}.jpg`,
      imageImagekit: category.imageImagekit || '',
      description: category.description || '',
      isActive: category.isActive !== false,
      showInCategoryGrid: useCatalogCategoryFlags ? true : category.showInCategoryGrid === true,
      showInCategoryNav: useCatalogCategoryFlags ? true : category.showInCategoryNav === true,
      displayOrder: category.displayOrder != null ? category.displayOrder : index
    }));

    const sortedCategories = sortCategoriesAlphabetically(transformedCategories);
    setCategories(sortedCategories);

    const navCategories = sortedCategories.filter((cat) => cat.showInCategoryNav === true);
    setCategoriesForNav(navCategories);

    const gridCategories = sortedCategories.filter((cat) => cat.showInCategoryGrid === true);
    setCategoriesForGrid(gridCategories);

    return sortedCategories;
  }, []);

  const extractProductsFromPageResponse = (response) => {
    if (Array.isArray(response)) return { pageList: response, pages: 1, total: response.length };
    let pageList = [];
    if (response.products && Array.isArray(response.products)) {
      pageList = response.products;
    } else if (response.data && Array.isArray(response.data)) {
      pageList = response.data;
    } else if (response.data?.products && Array.isArray(response.data.products)) {
      pageList = response.data.products;
    }
    const total = response.pagination?.total ?? response.total ?? pageList.length;
    const pages =
      response.pagination?.pages ?? Math.max(1, Math.ceil(total / CATALOG_PAGE_SIZE));
    return { pageList, pages, total };
  };

  const fetchAllMongoProductPages = async (onProgress) => {
    const firstResponse = await api.get(
      `/products?page=1&limit=${CATALOG_PAGE_SIZE}&view=card&isActive=true`
    );
    const first = extractProductsFromPageResponse(firstResponse);
    const allProducts = [...first.pageList];
    if (typeof onProgress === 'function' && allProducts.length) {
      onProgress(allProducts.slice());
    }
    const totalPages = Math.min(first.pages || 1, 200);

    if (totalPages > 1) {
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);
      const batchSize = 4;
      let skippedPages = 0;
      for (let i = 0; i < remainingPages.length; i += batchSize) {
        const batch = remainingPages.slice(i, i + batchSize);
        const results = await Promise.allSettled(
          batch.map((page) =>
            api.get(`/products?page=${page}&limit=${CATALOG_PAGE_SIZE}&view=card&isActive=true`)
          )
        );
        results.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            allProducts.push(...extractProductsFromPageResponse(result.value).pageList);
            return;
          }
          skippedPages += 1;
          const pageNum = batch[index];
          console.warn(
            `Storefront catalog: skipped product page ${pageNum}:`,
            result.reason?.message || result.reason
          );
        });
        if (typeof onProgress === 'function' && allProducts.length) {
          onProgress(allProducts.slice());
        }
      }
      if (skippedPages > 0) {
        console.warn(
          `Storefront catalog: loaded ${allProducts.length} products; ${skippedPages} page(s) failed (site stays usable).`
        );
      }
    }

    if (!allProducts.length) {
      throw new Error('No products returned from API');
    }

    return allProducts;
  };

  const fetchProducts = useCallback(async () => {
    if (productsFetchInflight.current) {
      return productsFetchInflight.current;
    }
    const p = (async () => {
      let publishedFirstPage = false;

      const publishProducts = (rawList, { markCatalogReady = false } = {}) => {
        const productsData = filterRawCatalogProducts(rawList);
        const transformedProducts = filterBrowsableProducts(productsData.map(transformProduct));
        setProducts(transformedProducts);
        setLastFetch(Date.now());
        setError(null);
        if (markCatalogReady && !publishedFirstPage && transformedProducts.length) {
          publishedFirstPage = true;
          setInitialCatalogLoaded(true);
          setLoading(false);
        }
        return transformedProducts;
      };

      const fetchProductsFromMongo = async () => {
        let productsData = [];
        try {
          const freshProducts = await fetchAllMongoProductPages((partial) => {
            publishProducts(partial, { markCatalogReady: true });
          });
          productsData = filterRawCatalogProducts(freshProducts);
          writeCatalogCache('products', productsData);
          if (process.env.NODE_ENV === 'development') {
            console.log(`Fetched ${productsData.length} products from MongoDB (card view)`);
          }
        } catch (fetchErr) {
          const cached = readCatalogCache('products');
          if (cached?.data?.length) {
            productsData = filterRawCatalogProducts(cached.data);
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                'Using session cache for products after fetch failure:',
                fetchErr?.message || fetchErr
              );
            }
          } else {
            throw fetchErr;
          }
        }
        return productsData;
      };

      try {
        const apiUrl = process.env.REACT_APP_API_URL;
        if (!apiUrl) {
          console.error('REACT_APP_API_URL is not set! Please configure it in environment variables.');
          setError('API URL not configured. Please set REACT_APP_API_URL in environment variables.');
          return [];
        }
        let productsData = [];
        let usedMongoFallback = false;
        if (useCatalog) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetching products from catalog (same as admin)...');
          }
          try {
            let page = 1;
            const limit = 100;
            let hasMore = true;
            while (hasMore) {
              const response = await api.get(`${productsPath}?page=${page}&limit=${limit}`);
              const list = Array.isArray(response) ? response : response.products || response.data || [];
              const arr = Array.isArray(list) ? list : [];
              productsData = productsData.concat(arr);
              if (page === 1 && productsData.length) {
                publishProducts(productsData, { markCatalogReady: true });
              } else if (page > 1) {
                publishProducts(productsData);
              }
              const total = response.pagination?.total ?? response.total ?? productsData.length;
              const pages = response.pagination?.pages ?? Math.max(1, Math.ceil(total / limit));
              hasMore = page < pages && arr.length === limit;
              page += 1;
              if (page > 100) break;
            }
            if (productsData.length === 0) {
              throw new Error('Catalog returned no products');
            }
            writeCatalogCache('products', filterRawCatalogProducts(productsData));
            if (process.env.NODE_ENV === 'development') {
              console.log(`Fetched ${productsData.length} products from catalog`);
            }
          } catch (catalogErr) {
            console.warn(
              'Catalog products unavailable, falling back to MongoDB:',
              catalogErr?.message || catalogErr
            );
            productsData = await fetchProductsFromMongo();
            usedMongoFallback = true;
          }
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.log('Fetching products from backend (MongoDB)...');
          }
          productsData = await fetchProductsFromMongo();
        }

        if (usedMongoFallback && process.env.NODE_ENV !== 'development') {
          console.warn(
            'Storefront loaded products from MongoDB because the catalog API is unavailable. Set REACT_APP_USE_CATALOG=false on Railway or fix CATALOG_API_URL on the backend.'
          );
        }

        const transformedProducts = publishProducts(productsData, { markCatalogReady: true });
        return transformedProducts;
      } catch (err) {
        console.error('Error fetching products:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          apiUrl: process.env.REACT_APP_API_URL
        });
        setError(sanitizeUserFacingError(`Failed to load products: ${err.message}`, 'Failed to load products. Please try again later.'));
        return [];
      } finally {
        if (productsFetchInflight.current === p) {
          productsFetchInflight.current = null;
        }
      }
    })();
    productsFetchInflight.current = p;
    return p;
  }, []);

  const fetchCategories = useCallback(async () => {
    if (categoriesFetchInflight.current) {
      return categoriesFetchInflight.current;
    }
    const p = (async () => {
      try {
        if (process.env.NODE_ENV === 'development') {
          console.log(
            useCatalog
              ? 'Fetching categories from catalog (same as admin)...'
              : 'Fetching categories from backend (MongoDB)...'
          );
        }
        let categoriesData = [];
        const cachedCategories = readCatalogCache('categories');
        if (cachedCategories?.data?.length) {
          categoriesData = cachedCategories.data;
          applyTransformedCategories(categoriesData, !useCatalog);
        }

        let categoriesFromMongo = !useCatalog;
        if (useCatalog) {
          try {
            const response = await api.get(categoriesPath);
            if (Array.isArray(response)) {
              categoriesData = response;
            } else if (response.data && Array.isArray(response.data)) {
              categoriesData = response.data;
            } else if (response.categories && Array.isArray(response.categories)) {
              categoriesData = response.categories;
            } else {
              categoriesData = [];
            }
            if (!categoriesData.length) {
              throw new Error('Catalog returned no categories');
            }
          } catch (catalogErr) {
            console.warn(
              'Catalog categories unavailable, falling back to MongoDB:',
              catalogErr?.message || catalogErr
            );
            categoriesFromMongo = true;
          }
        }

        if (categoriesFromMongo) {
          const response = await api.get('/categories');
          if (Array.isArray(response)) {
            categoriesData = response;
          } else if (response.data && Array.isArray(response.data)) {
            categoriesData = response.data;
          } else if (response.categories && Array.isArray(response.categories)) {
            categoriesData = response.categories;
          } else {
            categoriesData = [];
          }
        }

        writeCatalogCache('categories', categoriesData);
        if (process.env.NODE_ENV === 'development') {
          console.log(`Fetched ${categoriesData.length} categories from API`);
        }
        const transformedCategories = applyTransformedCategories(categoriesData, categoriesFromMongo);

        setError(null);
        return transformedCategories;
      } catch (err) {
        console.error('Error fetching categories:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          apiUrl: process.env.REACT_APP_API_URL
        });
        setError(sanitizeUserFacingError(`Failed to load categories: ${err.message}`, 'Failed to load categories. Please try again later.'));
        return [];
      } finally {
        if (categoriesFetchInflight.current === p) {
          categoriesFetchInflight.current = null;
        }
      }
    })();
    categoriesFetchInflight.current = p;
    return p;
  }, [applyTransformedCategories]);

  const loadSubcategories = useCallback(async (loadedProducts, loadedCategories) => {
    try {
      let mongoIdToRailwayId = {};
      if (useCatalog) {
        try {
          const mongoCatsResp = await api.get('/categories?includeInactive=true&includeDeleted=true');
          const mongoCats = Array.isArray(mongoCatsResp)
            ? mongoCatsResp
            : mongoCatsResp.data || mongoCatsResp.categories || [];
          mongoCats.forEach((c) => {
            if (!c?._id) return;
            mongoIdToRailwayId[String(c._id)] = c.railwayId || null;
          });
        } catch (e) {
          console.warn('Failed to fetch Mongo categories mapping for subcategories:', e?.message || e);
        }
      }

      const resp = await api.get('/subcategories');
      const list = Array.isArray(resp) ? resp : resp.data || resp.subcategories || [];
      const byCat = {};
      const nodesByCat = {};
      list.forEach((sub) => {
        const catId = sub.category;
        if (!catId) return;
        const mongoKey = String(catId);
        const node = {
          id: sub._id || sub.id,
          railwayId: sub.railwayId || null,
          name: formatCatalogDisplayName(sub.name),
          slug: sub.slug,
          isGroup: sub.isGroup === true,
          parentSubCategory: sub.parentSubCategory || null,
          displayOrder: sub.displayOrder ?? 0,
        };
        const keys = [mongoKey];
        const railwayKey = useCatalog ? mongoIdToRailwayId[mongoKey] : null;
        if (railwayKey) keys.push(String(railwayKey));
        keys.forEach((key) => {
          if (!nodesByCat[key]) nodesByCat[key] = [];
          nodesByCat[key].push(node);
        });
      });
      Object.keys(nodesByCat).forEach((k) => {
        byCat[k] = flattenLeafSubcategories(nodesByCat[k]);
      });
      const fromProducts = buildSubcategoriesFromProducts(loadedProducts, loadedCategories);
      setSubcategoriesByCategory(mergeSubcategoryMaps(byCat, fromProducts));
      setCategoryMenuTreeByCategory(buildCategoryMenuTreeByCategory(nodesByCat));
    } catch (err) {
      console.error('Error fetching subcategories for storefront:', err);
      const fromProducts = buildSubcategoriesFromProducts(loadedProducts, loadedCategories);
      setSubcategoriesByCategory(fromProducts);
      setCategoryMenuTreeByCategory({});
    }
  }, []);

  const fetchOutlets = useCallback(async () => {
    try {
      const response = await api.get('/outlets');
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.outlets)
            ? response.outlets
            : [];
      const normalized = list.filter(Boolean).map(normalizeOutlet);
      setOutlets(normalized);
      setStoreOutletsForChatLookup(normalizeOutletsForChatLookup(normalized));
      setInitialOutletsLoaded(true);
    } catch (err) {
      console.warn('Could not load outlets:', err);
      setInitialOutletsLoaded(true);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchProducts(), fetchCategories(), fetchOutlets()]);
    setLoading(false);
  }, [fetchProducts, fetchCategories, fetchOutlets]);

  const getProductsByCategory = useCallback(
    (categoryId) => {
      if (categoryId === 'all' || categoryId == null) return products;
      const want = String(categoryId);
      return products.filter((p) => {
        const catId =
          typeof p.category === 'object' && p.category ? p.category._id || p.category.id : p.category;
        if (catId == null) return false;
        return idsMatch(catId, want);
      });
    },
    [products]
  );

  const getFeaturedProducts = useCallback(
    () => products.filter((p) => p.isFeatured),
    [products]
  );

  const searchProducts = useCallback((query) => {
    if (!query) return products;
    const lowerQuery = query.toLowerCase();
    return products.filter(
      (p) =>
        p.name.toLowerCase().includes(lowerQuery) ||
        (p.description && p.description.toLowerCase().includes(lowerQuery)) ||
        (p.categoryName && p.categoryName.toLowerCase().includes(lowerQuery))
    );
  }, [products]);

  const getPromotionalProducts = useCallback(
    () => products.filter((p) => p.discount > 0),
    [products]
  );

  const getGeekProducts = useCallback(() => products.filter((p) => p.geek), [products]);

  /** Storefront listings: show out-of-stock; hide only inactive/deleted catalog rows. */
  const getAvailableProducts = useCallback(
    (productList = products) => filterBrowsableProducts(productList),
    [products]
  );

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        // Categories + outlets start immediately (lightweight).
        const categoriesPromise = fetchCategories();
        const outletsPromise = fetchOutlets();

        // Deprioritize full catalog so banner + section product APIs win the first paint.
        await yieldToHomepagePaint();
        if (cancelled) return;

        const productsPromise = fetchProducts();
        const [loadedProducts, loadedCategories] = await Promise.all([
          productsPromise,
          categoriesPromise,
          outletsPromise,
        ]);
        if (cancelled) return;
        setInitialCatalogLoaded(true);
        setLoading(false);
        void loadSubcategories(loadedProducts || [], loadedCategories || []);
      } catch (err) {
        console.error('Error loading storefront catalog:', err);
        if (!cancelled) setLoading(false);
      }
    };
    loadData();
    return () => {
      cancelled = true;
    };
  }, [fetchProducts, fetchCategories, loadSubcategories, fetchOutlets]);

  useEffect(() => {
    const id = setInterval(() => {
      void Promise.all([fetchProducts(), fetchCategories(), fetchOutlets()]);
    }, BACKGROUND_POLL_MS);
    return () => clearInterval(id);
  }, [fetchProducts, fetchCategories, fetchOutlets]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastTabRefreshAttemptRef.current < MIN_TAB_FOCUS_REFRESH_GAP_MS) return;
      lastTabRefreshAttemptRef.current = now;
      void Promise.all([fetchProducts(), fetchCategories(), fetchOutlets()]);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchProducts, fetchCategories, fetchOutlets]);

  const value = useMemo(
    () => ({
      products,
      categories,
      categoriesForGrid,
      categoriesForNav,
      subcategoriesByCategory,
      categoryMenuTreeByCategory,
      loading,
      initialCatalogLoaded,
      outlets,
      initialOutletsLoaded,
      error,
      lastFetch,
      refresh,
      getProductsByCategory,
      getFeaturedProducts,
      searchProducts,
      getPromotionalProducts,
      getGeekProducts,
      getAvailableProducts,
      fetchProducts,
      fetchCategories,
      fetchOutlets,
    }),
    [
      products,
      categories,
      categoriesForGrid,
      categoriesForNav,
      subcategoriesByCategory,
      categoryMenuTreeByCategory,
      loading,
      initialCatalogLoaded,
      outlets,
      initialOutletsLoaded,
      error,
      lastFetch,
      refresh,
      getProductsByCategory,
      getFeaturedProducts,
      searchProducts,
      getPromotionalProducts,
      getGeekProducts,
      getAvailableProducts,
      fetchProducts,
      fetchCategories,
      fetchOutlets,
    ]
  );

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    console.log('DataContext snapshot:', {
      productsCount: products.length,
      categoriesCount: categories.length,
      loading,
      error,
      useCatalog,
      backgroundPollMs: BACKGROUND_POLL_MS,
      minFocusRefreshMs: MIN_TAB_FOCUS_REFRESH_GAP_MS
    });
  }, [products.length, categories.length, loading, error]);

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};
